<template>
  <div class="team-preview-popup">
    <div :class="{ 'mode-my-team': mode === 'my-team' }" class="popup-header-container">
      <div class="popup-header">
        <div class="header-left-container">
          <div class="header-left">
            <span class="back-btn" @click="handleClose">
              <van-icon name="cross" />
            </span>
          </div>
          <div class="team-title">
            <div class="team-owner">
              <span class="user-id">User{{ displayUserId }}</span>
              <span class="team-name">({{ displayTeamName }})</span>
              <span class="team-no">T{{ displayTeamNo }}</span>
            </div>
            <template v-if="isMatchStarted">
              <div class="team-points">{{ totalPoints || '--' }} <span class="pts-text">pts</span></div>
            </template>
          </div>
        </div>

        <div class="header-right">
          <div class="action-bar">
            <div v-if="showActionBtn" class="action-item" @click="handleEdit">
              <div class="action-icon edit-icon">
                <SvgIcon name="edit" width="20px" class="badge-icon" />
              </div>
            </div>
            <div class="action-item" @click="handleShare">
              <div class="action-icon share-icon">
                <SvgIcon name="share" width="20px" class="badge-icon" />
              </div>
            </div>
            <div class="action-item" @click="handlePts">
              <SvgIcon name="pts" width="20px" class="badge-icon" />
            </div>
          </div>
        </div>
      </div>
      <MatchBriefCard v-if="!isMatchStarted"
        :match-info="{ ...matchInfo, teamAPlayerCount: teamPlayerCounts.teamACount, teamBPlayerCount: teamPlayerCounts.teamBCount }"
        :selected-players-count="players.length" :used-credits="usedCredits" />
    </div>



    <div class="pitch-container">
      <div class="pitch-bg">
        <div class="position-section wk-section">
          <div class="section-label"> <span class="section-label-text">WICKET-KEEPERS</span> </div>
          <div class="players-row">
            <PreviewPlayerCard :match-info="matchInfo" v-for="player in groupedPlayers.WK"
              :key="player.pid || player.id" :player="player" @click="handlePlayerClick" />
          </div>
        </div>

        <div class="position-section bat-section">
          <div class="section-label"> <span class="section-label-text">BATTERS</span> </div>
          <div class="players-row">
            <PreviewPlayerCard :match-info="matchInfo" v-for="player in groupedPlayers.BAT"
              :key="player.pid || player.id" :player="player" @click="handlePlayerClick" />
          </div>
        </div>

        <div class="position-section ar-section">
          <div class="section-label"> <span class="section-label-text">ALL-ROUNDERS</span> </div>
          <div class="players-row">
            <PreviewPlayerCard :match-info="matchInfo" v-for="player in groupedPlayers.AR"
              :key="player.pid || player.id" :player="player" @click="handlePlayerClick" />
          </div>
        </div>

        <div class="position-section bowl-section">
          <div class="section-label"> <span class="section-label-text">BOWLERS</span> </div>
          <div class="players-row">
            <PreviewPlayerCard :match-info="matchInfo" v-for="player in groupedPlayers.BOWL"
              :key="player.pid || player.id" :player="player" @click="handlePlayerClick" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="isMatchStarted" class="preview-footer">
      <div class="match-teams">
        <span class="team-a"><span :class="{ 'team-dot-active': teamInfo.teamaIsDreamTeam }" class="team-dot"></span>{{
          matchInfo.teamAShort || '--' }}</span>
        <span class="team-b"> <span :class="{ 'team-dot-active': teamInfo.teambIsDreamTeam }" class="team-dot"></span>{{
          matchInfo.teamBShort || '--' }} </span>
      </div>
      <div v-if="teamInfo.teambIsDreamTeam || teamInfo.teamaIsDreamTeam" class="dream-team-badge">
        In Dream Team
        <SvgIcon name="dt" class="dt-icon" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import PreviewPlayerCard from './PreviewPlayerCard.vue'
import MatchBriefCard from './MatchBriefCard.vue'
import SvgIcon from '@/components/SvgIcon/index.vue'
import { MatchStatus } from '@/views/contests/matchesType.js'

const props = defineProps({
  mode: {
    type: String,
    default: 'my-team',
  },
  playerData: {
    type: Array,
    default: () => [],
  },
  matchInfo: {
    type: Object,
    default: () => ({}),
  },
  teamMeta: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(['close', 'edit', 'share', 'pts', 'playerClick'])

const isMatchStarted = computed(() => props.matchInfo.matchStatus !== MatchStatus.NOT_STARTED)
const players = computed(() => props.playerData || [])

const teamInfo = computed(() => {
  const teamaId = props.matchInfo.teamaId
  const teambId = props.matchInfo.teambId
  return {
    teamaIsDreamTeam: players.value.some(player => player.teamId === teamaId && player.dreamTeam === 1),
    teambIsDreamTeam: players.value.some(player => player.teamId === teambId && player.dreamTeam === 1),
  }
})

const displayUserId = computed(() => props.teamMeta?.userId ?? '--')
const displayTeamName = computed(() => props.teamMeta?.teamName || '--')
const displayTeamNo = computed(() => props.teamMeta?.teamNo ?? '--')

const totalPoints = computed(() => {
  if (!isMatchStarted.value) {
    return players.value.reduce((sum, p) => sum + (p.rating || 0), 0)
  }
  console.error(players.value);

  return players.value.reduce((sum, p) => sum + (p.points || 0), 0)
})

const showActionBtn = computed(() => {
  if (props.mode === 'my-team') return false
  return !isMatchStarted.value
})

const usedCredits = computed(() => {
  return players.value.reduce((sum, p) => sum + (p.rating || 0), 0)
})

const teamPlayerCounts = computed(() => {
  const teamaId = props.matchInfo?.teamaId
  const teambId = props.matchInfo?.teambId
  const teamACount = players.value.filter(p => p.teamId === teamaId).length
  const teamBCount = players.value.filter(p => p.teamId === teambId).length
  return { teamACount, teamBCount }
})

const groupedPlayers = computed(() => {
  const groups = { WK: [], BAT: [], AR: [], BOWL: [] }
  players.value.forEach((player) => {
    if (groups[player.position]) {
      groups[player.position].push(player)
    }
  })
  return groups
})

const handleClose = () => emit('close')
const handleEdit = () => emit('edit')
const handleShare = () => emit('share')
const handlePts = () => emit('pts')
const handlePlayerClick = (player) => emit('playerClick', player)
</script>

<style lang="less" scoped>
.team-preview-popup {
  max-height: 100vh;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: url('/src/assets/images/team-preview-bg-D-9qbkqb.webp') no-repeat center;
  background-size: 100% auto;
  overflow-y: auto;

  .popup-header-container {
    &.mode-my-team {
      background: #18212e;
    }
  }


  .popup-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 5px 16px;
    color: #fff;
    width: 100%;
    z-index: 1;
    background: linear-gradient(to top, rgba(66, 104, 194, 0) 0%, rgba(0, 0, 0, 0.2) 100%);


    .header-left-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }

    .back-btn {
      font-size: 20px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
    }

    .team-title {
      .team-owner {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .user-id {
        font-size: 12px;
        font-weight: 600;
        font-family: CadillacTrimV1-SemiBold, sans-serif;
      }

      .team-name {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.9);
      }

      .team-no {
        font-size: 11px;
        padding: 2px 6px;
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.2);
        color: #fff;
      }
    }

    .team-title .team-points {
      font-size: 20px;
      font-weight: 600;
    }

    .action-bar {
      display: flex;
      align-items: flex-end;
      gap: 10px;
    }
  }

  .pitch-container {
    flex: 1;
    padding: 16px;
    position: relative;
    background: url('/src/assets/images/team-preview-bg-D-9qbkqb.webp') no-repeat center;
    background-size: 100% auto;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: url('/src/assets/images/team-preview-bg-DLs6Yne5.webp') no-repeat center;
      background-size: 100% auto;
    }

    .pitch-bg {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      gap: 20px;
      min-height: 100%;
    }

    .position-section .section-label {
      text-align: center;
      margin-bottom: 10px;
    }

    .position-section .section-label-text {
      font-size: 9px;
      color: rgba(255, 255, 255, 0.8);
      margin: 5px auto;
      letter-spacing: 1px;
      display: inline-block;
      text-transform: uppercase;
      border-radius: 10px;
      background: linear-gradient(90deg, rgba(24, 29, 41, 0), rgba(24, 29, 41, .26) 22.8%, rgba(24, 29, 41, .26) 49.52%, rgba(24, 29, 41, .26) 76.3%, rgba(24, 29, 41, 0) 98.08%);
      font-family: Heebo-SemiBold, sans-serif;
      padding: 2px 10px;
    }

    .players-row {
      display: flex;
      justify-content: center;
      gap: 16px;
      flex-wrap: wrap;
    }
  }

  .preview-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: #1a5c2a;
    color: #fff;
  }

  .team-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #969696;
    margin-right: 4px;
    display: inline-block;
  }

  .team-dot-active {
    background: #ffffff;
  }
}
</style>
